import { useEffect, useRef, useState } from "react";
import { Plus, Paperclip, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { Stars } from "@/features/rfq/components/Stars";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { toDatetimeLocalValue } from "@/utils/datetime";
import { useScopeCategories, useVoivodeships } from "@/features/rfq/api/scopes";
import { CountyRefinement } from "./CountyRefinement";
import { fileTooLargeMessage, findTooLargeFile, uploadErrorMessage } from "@/utils/managedFile";
import {
  ACCESS_MODES,
  useAddAttachment,
  useBuyerOptions,
  useContractorOptions,
  useDefaultDeclarationQuestions,
  useProjectOptions,
  useRatedContractors,
  useRemoveAttachment,
  useSaveProceeding,
  type AccessMode,
  type Proceeding,
} from "@/features/rfq/api/proceedings";

/**
 * Formularz szkicu postępowania — wspólny dla listy (nowe + edycja) i panelu
 * postępowania (edycja szkicu bez wracania na listę). Po publikacji treść jest
 * zamrożona, więc modal ma sens wyłącznie dla statusu „szkic".
 */

const EMPTY: Partial<Proceeding> = {
  title: "",
  description: "",
  access_mode: "qualified",
  max_price_visible: false,
  price_basis: "",
  positions: [],
  declaration_questions: [],
  invited_contractors: [],
  excluded_contractors: [],
  is_public: false,
  voivodeship: "",
  counties: [],
};

export function ProceedingFormModal({
  initial,
  onClose,
}: {
  initial: Partial<Proceeding>;
  onClose: () => void;
}) {
  const { data: categories } = useScopeCategories();
  const { data: voivodeships } = useVoivodeships();
  const { data: projects } = useProjectOptions();
  const { data: buyers } = useBuyerOptions();
  const { data: contractorOptions } = useContractorOptions();
  // Firmy z ocenami — źródło pickera wykluczeń. Lista bywa długa, więc żyje tu,
  // a nie na stronach: pobiera się dopiero z otwartym formularzem.
  const { data: ratedContractors } = useRatedContractors(true);
  const addAttachment = useAddAttachment();
  const removeAttachment = useRemoveAttachment();
  const save = useSaveProceeding();

  const [form, setForm] = useState<Partial<Proceeding>>({ ...EMPTY, ...initial });
  // Nowy szkic startuje z aktywnymi oświadczeniami z biblioteki — modal jest w tym
  // samowystarczalny, więc każde miejsce otwierające go „na nowo" dostaje je za darmo.
  const { data: defaultQuestions } = useDefaultDeclarationQuestions();
  const defaultsApplied = useRef(
    initial.id !== undefined || initial.declaration_questions !== undefined,
  );
  useEffect(() => {
    if (defaultsApplied.current || !defaultQuestions) return;
    defaultsApplied.current = true;
    setForm((f) => ({
      ...f,
      declaration_questions: defaultQuestions.filter((q) => q.is_active).map((q) => q.text),
    }));
  }, [defaultQuestions]);
  const [error, setError] = useState("");
  // Powiaty realizacji: null = całe województwo (stan domyślny i zapis pustej listy);
  // lista = zawężenie. Osobno od `form`, bo pusta lista w formularzu znaczy „nic nie
  // wybrano" (błąd), a w API „całe województwo" — nie wolno ich pomylić przy zapisie.
  const [countyPick, setCountyPick] = useState<string[] | null>(
    initial.counties?.length ? initial.counties : null,
  );
  // Nowe załączniki żyją lokalnie do czasu zapisania szkicu — endpoint wisi na
  // istniejącym ogłoszeniu, więc przy zakładaniu nie ma jeszcze czego obwiesić.
  const [newFiles, setNewFiles] = useState<{ label: string; file: File | null }[]>([]);
  const [excludeSearch, setExcludeSearch] = useState("");

  const submit = () => {
    if (!form.title?.trim()) return setError("Tytuł jest wymagany.");
    if (!form.submission_deadline) return setError("Termin składania ofert jest wymagany.");
    if (!form.delivery_deadline) return setError("Termin realizacji jest wymagany.");
    if (form.delivery_deadline < toDatetimeLocalValue(form.submission_deadline).slice(0, 10))
      return setError("Termin realizacji musi być późniejszy niż termin składania ofert.");
    if (form.voivodeship && countyPick !== null && countyPick.length === 0)
      return setError("Wskaż co najmniej jeden powiat realizacji albo zostaw całe województwo.");
    const pending = newFiles.filter((row) => row.file);
    // Before the save — the registry refuses it anyway, after the draft already exists.
    const tooLarge = findTooLargeFile(pending.map((row) => row.file!));
    if (tooLarge) return setError(fileTooLargeMessage(tooLarge));
    setError("");
    save.mutate(
      {
        id: form.id,
        title: form.title.trim(),
        description: form.description ?? "",
        submission_deadline: form.submission_deadline,
        delivery_deadline: form.delivery_deadline || null,
        max_price_net: form.max_price_net || null,
        max_price_visible: !!form.max_price_visible,
        price_basis: form.price_basis ?? "",
        access_mode: form.access_mode ?? "qualified",
        responsible: form.responsible || null,
        project: form.project || null,
        positions: form.positions ?? [],
        declaration_questions: form.declaration_questions ?? [],
        invited_contractors: form.invited_contractors ?? [],
        excluded_contractors: form.excluded_contractors ?? [],
        // Publiczna strona wolno tylko przy trybie jawnym — checkbox znika po
        // zmianie trybu, więc jego zastany stan nie może wywrócić zapisu.
        is_public: form.access_mode === "open" && !!form.is_public,
        voivodeship: form.voivodeship ?? "",
        counties: form.voivodeship ? (countyPick ?? []) : [],
      },
      {
        onSuccess: async (saved) => {
          const id = form.id ?? saved.id;
          // From now on the draft exists — a retry after a failed attachment must
          // update it, not create a second one.
          if (!form.id) setForm((f) => ({ ...f, id }));
          for (const row of pending) {
            try {
              await addAttachment.mutateAsync({
                proceedingId: id,
                label: row.label.trim() || row.file!.name,
                file: row.file!,
              });
            } catch (err) {
              // Szkic już zapisany; nie zamykaj formularza po cichu, gdy plik nie wszedł.
              setError(
                `Postępowanie zapisane, ale nie udało się dograć załącznika „${row.file!.name}”: ` +
                  uploadErrorMessage(err, "spróbuj ponownie."),
              );
              return;
            }
            // Uploaded — a retry must not attach it twice.
            setNewFiles((rows) => rows.filter((r) => r !== row));
          }
          onClose();
        },
        onError: () => setError("Nie udało się zapisać. Sprawdź dane."),
      },
    );
  };

  const toggleIn = (list: string[] | undefined, value: string) =>
    (list ?? []).includes(value)
      ? (list ?? []).filter((v) => v !== value)
      : [...(list ?? []), value];

  // Słownikowe województwo realizacji — z kompletem powiatów do zawężenia.
  const countyOption = form.voivodeship
    ? (voivodeships ?? []).find((v) => v.value === form.voivodeship)
    : undefined;

  return (
    <Modal
      open
      onClose={onClose}
      title={form.id ? `Edycja ${form.number}` : "Nowe postępowanie"}
      size="lg"
    >
      <div className="space-y-6">
        <Group title="Przedmiot">
          <Field label="Tytuł">
            <input
              autoFocus
              value={form.title ?? ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className={inputClass}
              placeholder="np. Budowa linii kablowej nn — Sikorzyno"
            />
          </Field>
          <Field label="Opis">
            <textarea
              value={form.description ?? ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4}
              className={inputClass}
              placeholder="Zakres prac, wymagania, warunki odbioru…"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Kupujący" hint="Do niego oferenci kierują pytania.">
              <SearchableSelect
                value={form.responsible ?? ""}
                onChange={(e) => setForm({ ...form, responsible: e.target.value })}
                placeholder="— wybierz —"
                options={(buyers ?? []).map((b) => ({
                  value: b.id,
                  label: [b.first_name, b.last_name].filter(Boolean).join(" ") || b.username,
                }))}
              />
            </Field>
            <Field
              label="Przedsięwzięcie"
              hint="Tylko dla GK — oferent tego nie zobaczy."
            >
              <SearchableSelect
                value={form.project ?? ""}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
                placeholder="— bez powiązania —"
                options={[
                  { value: "", label: "— bez powiązania —" },
                  ...(projects ?? []).map((p) => ({ value: p.id, label: p.name })),
                ]}
              />
            </Field>
          </div>
        </Group>

        <Group title="Terminy">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Termin składania ofert">
              <input
                type="datetime-local"
                value={toDatetimeLocalValue(form.submission_deadline)}
                onChange={(e) =>
                  setForm({
                    ...form,
                    submission_deadline: e.target.value
                      ? new Date(e.target.value).toISOString()
                      : "",
                  })
                }
                className={inputClass}
              />
            </Field>
            <Field label="Termin realizacji" hint="Musi być późniejszy niż termin składania ofert.">
              <DatePicker
                value={form.delivery_deadline}
                onChange={(value) => setForm({ ...form, delivery_deadline: value })}
              />
            </Field>
          </div>
        </Group>

        <Group title="Cena maksymalna">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Kwota netto" hint="Opcjonalna.">
              <input
                type="number"
                step="0.01"
                value={form.max_price_net ?? ""}
                onChange={(e) => setForm({ ...form, max_price_net: e.target.value })}
                className={inputClass}
                placeholder="0,00"
              />
            </Field>
            <div className="pt-[22px]">
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!form.max_price_visible}
                  onChange={(e) =>
                    setForm({ ...form, max_price_visible: e.target.checked })
                  }
                />
                <span>
                  <span className="text-[var(--color-text-primary)]">Jawna dla oferentów</span>
                  <span className="block text-xs text-[var(--color-text-muted)]">
                    Jawna działa jak twardy limit. Ukryta zostaje po naszej stronie —
                    blokowanie zdradziłoby kwotę.
                  </span>
                </span>
              </label>
            </div>
          </div>
          <Field
            label="Czego dotyczy cena"
            hint="Widzi to oferent — także wtedy, gdy kwota maksymalna jest ukryta lub nie ma jej wcale. Puste = cena za całość zamówienia."
          >
            <input
              type="text"
              maxLength={200}
              value={form.price_basis ?? ""}
              onChange={(e) => setForm({ ...form, price_basis: e.target.value })}
              className={inputClass}
              placeholder="np. za komplet 100 działek / za 1 działkę"
            />
          </Field>
        </Group>

        <Group title="Kto może startować">
          <Field label="Tryb dostępu">
            <div className="space-y-1.5">
              {ACCESS_MODES.map((mode) => (
                <label
                  key={mode.value}
                  className="flex items-start gap-2 border border-[var(--color-border)] p-2 text-sm hover:bg-[var(--color-bg-elevated)]"
                >
                  <input
                    type="radio"
                    name="access_mode"
                    className="mt-1"
                    checked={form.access_mode === mode.value}
                    onChange={() =>
                      setForm({ ...form, access_mode: mode.value as AccessMode })
                    }
                  />
                  <span>
                    <span className="text-[var(--color-text-primary)]">{mode.label}</span>
                    <span className="block text-xs text-[var(--color-text-muted)]">
                      {mode.hint}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </Field>

          <Field
            label="Województwo realizacji"
            hint="Puste = obszar nie jest kryterium (np. praca w pełni zdalna)."
          >
            <SearchableSelect
              value={form.voivodeship ?? ""}
              onChange={(e) => {
                setForm({ ...form, voivodeship: e.target.value });
                // Inne województwo = inne powiaty; zawężenie startuje od nowa (całość).
                setCountyPick(null);
              }}
              placeholder="— bez znaczenia —"
              options={[
                { value: "", label: "— bez znaczenia —" },
                ...(voivodeships ?? []).map((v) => ({ value: v.value, label: v.label })),
              ]}
            />
          </Field>

          {countyOption && (
            <Field
              label="Powiaty realizacji"
              hint="Domyślnie całe województwo. Zawężenie odsiewa firmy, które nie działają w każdym z tych powiatów."
            >
              <CountyRefinement
                option={countyOption}
                selected={countyPick ?? countyOption.counties.map((c) => c.teryt)}
                onChange={(next) =>
                  setCountyPick(next.length === countyOption.counties.length ? null : next)
                }
                verb="zawęź"
                indent={false}
              />
            </Field>
          )}

          {form.access_mode === "invited" && (
            <Field
              label="Zaproszone firmy"
              hint="Tylko te firmy zobaczą ogłoszenie."
            >
              <div className="max-h-48 space-y-1 overflow-y-auto border border-[var(--color-border)] p-2">
                {(contractorOptions ?? []).map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(form.invited_contractors ?? []).includes(c.id)}
                      onChange={() =>
                        setForm({
                          ...form,
                          invited_contractors: toggleIn(form.invited_contractors, c.id),
                        })
                      }
                    />
                    <span className="text-[var(--color-text-primary)]">{c.name}</span>
                  </label>
                ))}
              </div>
            </Field>
          )}

          {/* Wykluczenia — świadomie POZA trybem „dla wskazanych": tam krąg buduje
              się listą zaproszonych, więc odejmowanie byłoby dwoma sposobami na to
              samo. To NIE jest blokada firmy: dotyczy wyłącznie tego ogłoszenia. */}
          {form.access_mode !== "invited" && (
            <Field
              label="Firmy wykluczone z tego postępowania"
              hint="Nie zobaczą ogłoszenia i nie złożą oferty. Nie odbiera kwalifikacji ani nie blokuje firmy — dotyczy tylko tego zapytania."
            >
              <div className="relative">
                <input
                  value={excludeSearch}
                  onChange={(e) => setExcludeSearch(e.target.value)}
                  placeholder="Szukaj firmy…"
                  className="mb-1 w-full border border-[var(--color-border)] bg-transparent px-3 py-1.5 text-sm text-[var(--color-text-primary)]"
                />
                <div className="max-h-56 space-y-1 overflow-y-auto border border-[var(--color-border)] p-2">
                  {(ratedContractors ?? [])
                    .filter(
                      (c) =>
                        !excludeSearch.trim() ||
                        c.name
                          .toLowerCase()
                          .includes(excludeSearch.trim().toLowerCase()),
                    )
                    .map((c) => (
                      <label
                        key={c.id}
                        className="flex items-center justify-between gap-3 text-sm"
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={(form.excluded_contractors ?? []).includes(c.id)}
                            onChange={() =>
                              setForm({
                                ...form,
                                excluded_contractors: toggleIn(
                                  form.excluded_contractors,
                                  c.id,
                                ),
                              })
                            }
                          />
                          <span className="text-[var(--color-text-primary)]">
                            {c.name}
                          </span>
                        </span>
                        {/* Gwiazdki przy nazwie: decyzja „tej firmy tu nie chcę"
                            zapada na tym, jak się sprawdziła. */}
                        <span className="flex shrink-0 items-center gap-1.5">
                          {c.rating === null ? (
                            <span className="text-xs text-[var(--color-text-muted)]">
                              brak ocen
                            </span>
                          ) : (
                            <>
                              <Stars value={c.rating} size={11} />
                              <span className="text-xs text-[var(--color-text-muted)]">
                                {c.rating.toFixed(1)} ({c.rating_count})
                              </span>
                            </>
                          )}
                        </span>
                      </label>
                    ))}
                </div>
              </div>
            </Field>
          )}

          {/* Publiczna strona pokazuje ogłoszenie każdemu przechodniowi, więc
              wolno tam wystawić wyłącznie postępowanie w pełni jawne. */}
          {form.access_mode === "open" && (
            <Field
              label="Publiczna strona"
              hint="Ogłoszenie trafi na otwartą listę pod /postepowania — zobaczą je także firmy bez konta. Oferty nadal wymagają konta."
            >
              <label className="flex items-start gap-2 text-sm text-[var(--color-text-secondary)]">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!form.is_public}
                  onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
                />
                <span>
                  Pokaż publicznie (bez logowania)
                  <span className="block text-xs text-[var(--color-text-muted)]">
                    Na zewnątrz idą tylko warunki — bez załączników i bez kontaktu
                    do kupującego. Przycisk „Złóż ofertę" prowadzi do prośby o konto.
                  </span>
                </span>
              </label>
            </Field>
          )}

          <Field
            label="Zakres prac"
            hint="Konkretne pozycje z katalogu. Przy trybie dla prekwalifikowanych firma musi pokrywać wszystkie zaznaczone."
          >
            <div className="max-h-64 space-y-3 overflow-y-auto border border-[var(--color-border)] p-2">
              {(categories ?? []).map((category) => (
                <div key={category.id}>
                  <p className="text-xs font-semibold text-[var(--color-text-secondary)]">
                    {category.name}
                  </p>
                  <div className="ml-2 mt-1 space-y-0.5">
                    {category.scopes.length === 0 ? (
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Brak pozycji w tej kategorii.
                      </p>
                    ) : (
                      category.scopes.map((scope) => (
                        <label key={scope.id} className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={(form.positions ?? []).includes(scope.id)}
                            onChange={() =>
                              setForm({
                                ...form,
                                positions: toggleIn(form.positions, scope.id),
                              })
                            }
                          />
                          <span className="text-[var(--color-text-primary)]">
                            {scope.name}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Field>
        </Group>

        <Group title="Oświadczenia oferenta">
          <p className="text-xs text-[var(--color-text-muted)]">
            Pytania TAK/NIE, na które oferent musi odpowiedzieć przy składaniu oferty.
            Wypełnione domyślnymi — dodaj, usuń lub zmień pod to postępowanie.
          </p>
          <div className="mt-2 space-y-2">
            {(form.declaration_questions ?? []).map((q, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  value={q}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      declaration_questions: (form.declaration_questions ?? []).map((x, i) =>
                        i === index ? e.target.value : x,
                      ),
                    })
                  }
                  className={`${inputClass} flex-1`}
                />
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      ...form,
                      declaration_questions: (form.declaration_questions ?? []).filter(
                        (_, i) => i !== index,
                      ),
                    })
                  }
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() =>
                setForm({
                  ...form,
                  declaration_questions: [...(form.declaration_questions ?? []), ""],
                })
              }
              className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
            >
              <Plus size={12} /> Dodaj oświadczenie
            </button>
          </div>
        </Group>

        <Group title="Załączniki">
          {(form.attachments ?? []).length > 0 && (
            <ul className="divide-y divide-[var(--color-border)] border border-[var(--color-border)]">
              {(form.attachments ?? []).map((attachment) => (
                <li key={attachment.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <Paperclip size={12} className="text-[var(--color-text-muted)]" />
                  <span className="flex-1 text-[var(--color-text-primary)]">
                    {attachment.label || attachment.original_filename}
                  </span>
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {attachment.original_filename}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      removeAttachment.mutate(
                        { proceedingId: form.id!, attachmentId: attachment.id },
                        {
                          onSuccess: () =>
                            setForm({
                              ...form,
                              attachments: (form.attachments ?? []).filter(
                                (a) => a.id !== attachment.id,
                              ),
                            }),
                        },
                      )
                    }
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-2">
            {newFiles.map((row, index) => (
              <div key={index} className="flex flex-wrap items-center gap-2">
                <input
                  value={row.label}
                  onChange={(e) =>
                    setNewFiles((prev) =>
                      prev.map((r, i) => (i === index ? { ...r, label: e.target.value } : r)),
                    )
                  }
                  placeholder="Nazwa załącznika (np. Przedmiar robót)"
                  className={`${inputClass} flex-1 min-w-[12rem]`}
                />
                <div className="min-w-[14rem] flex-1">
                  <FileDropZone
                    files={row.file ? [row.file] : []}
                    onFilesChange={(files) =>
                      setNewFiles((prev) =>
                        prev.map((r, i) => (i === index ? { ...r, file: files[0] ?? null } : r)),
                      )
                    }
                    label="Przeciągnij plik lub kliknij"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== index))}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setNewFiles((prev) => [...prev, { label: "", file: null }])}
              className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
            >
              <Plus size={12} /> Dodaj załącznik
            </button>
            <p className="text-xs text-[var(--color-text-muted)]">
              Pliki dołączają się po zapisaniu szkicu. Bez nazwy użyjemy nazwy pliku.
            </p>
          </div>
        </Group>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] pt-4">
          <Button variant="secondary" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={submit} disabled={save.isPending || addAttachment.isPending}>
            {save.isPending ? "Zapisywanie…" : addAttachment.isPending ? "Wgrywanie załączników…" : "Zapisz szkic"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const inputClass =
  "w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]";

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="border-l-2 border-[var(--color-primary)] pl-2 text-sm font-semibold text-[var(--color-text-primary)]">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-[var(--color-text-muted)]">{hint}</p>}
    </div>
  );
}
